import type { QueryClient } from '@tanstack/react-query';
import type { ChatStreamEvent, QuestionOption, WireChatMessage } from '@logistics/contracts';
import type { Ask, LiveTurn } from './live-turn';
import { now } from '@/lib/clock';
import { failureFrom } from '@/lib/result';
import { streamChat } from './stream-client';
import { CONVERSATIONS_KEY, conversationKey } from './use-chat-data';
import type { ConversationData } from './use-chat-data';

export interface TurnContext {
  readonly client: QueryClient;
  readonly me: { readonly id: string; readonly name: string };
  readonly activeId: string | null;
  readonly signal: AbortSignal;
  readonly key: number;
  readonly start: (turn: LiveTurn) => void;
  readonly update: (patch: Partial<LiveTurn>) => void;
  readonly setActiveId: (id: string) => void;
  readonly onAnswered: (message: WireChatMessage) => void;
}

function appendMessages(client: QueryClient, id: string, messages: WireChatMessage[]): void {
  client.setQueryData<ConversationData>(conversationKey(id), (old) =>
    old === undefined ? old : { ...old, messages: [...old.messages, ...messages] },
  );
}

/**
 * One question, start to end. On `done` the question and the answer are
 * written into the conversation's cache in the positions the live turn held;
 * a new conversation's cache is seeded from the `conversation` event, so the
 * thread never re-reads a conversation while its answer is still coming.
 */
export async function runTurn(ask: Ask, ctx: TurnContext): Promise<void> {
  let id = ctx.activeId ?? undefined;
  const createdAt = now().toISOString();
  const user: WireChatMessage = {
    id: `local-${ctx.key}`,
    conversationId: id ?? '',
    role: 'user',
    text: ask.text,
    citations: [],
    attachments: [...ask.attachments],
    createdAt,
  };
  const fail = (message: string): void => {
    if (id !== undefined) appendMessages(ctx.client, id, [{ ...user, conversationId: id }]);
    ctx.update({ error: message });
  };
  ctx.start({ key: ctx.key, conversationId: id, user });

  let finished = false;
  const onEvent = (event: ChatStreamEvent): void => {
    if (event.type === 'conversation') {
      if (id === undefined) {
        id = event.conversationId;
        seedConversation(ctx, id, event.title, ask.scope, createdAt);
        ctx.update({ conversationId: id });
        ctx.setActiveId(id);
      }
      void ctx.client.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    } else if (event.type === 'step') {
      ctx.update({ step: event });
    } else if (event.type === 'done' && id !== undefined) {
      finished = true;
      appendMessages(ctx.client, id, [{ ...user, conversationId: id }, event.message]);
      ctx.update({ answered: event.message.id });
      ctx.onAnswered(event.message);
      void ctx.client.invalidateQueries({ queryKey: conversationKey(id) });
      void ctx.client.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
    } else if (event.type === 'error') {
      finished = true;
      fail(event.message);
    }
  };

  try {
    const attachmentIds = ask.attachments.map((a) => a.id);
    await streamChat(
      { conversationId: id, text: ask.text, scope: ask.scope, attachmentIds },
      ctx.signal,
      onEvent,
    );
    if (!finished && !ctx.signal.aborted) fail('the answer stopped before it finished');
  } catch (error) {
    if (ctx.signal.aborted) return;
    const { status, detail } = failureFrom(error);
    fail(
      detail ?? (status === undefined ? 'the API did not answer' : `the API answered ${status}`),
    );
  }
}

function seedConversation(
  ctx: TurnContext,
  id: string,
  title: string,
  scope: readonly QuestionOption[],
  at: string,
): void {
  const conversation = {
    id,
    owner: { id: ctx.me.id, name: ctx.me.name },
    title,
    scope: [...scope],
    createdAt: at,
    updatedAt: at,
  };
  ctx.client.setQueryData<ConversationData>(conversationKey(id), { conversation, messages: [] });
}
